<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3" v-if="device">
    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5 class="mb-3">بيانات الطالب</h5>
          <p><strong>الاسم:</strong> {{ device.student_name ?? '-' }}</p>
          <p><strong>السنة الدراسية:</strong> {{ device.academic_year ?? '-' }}</p>
          <p><strong>المجموعة:</strong> {{ device.group ?? '-' }}</p>
          <p><strong>الهاتف:</strong> {{ device.phone ?? '-' }}</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5 class="mb-3">بيانات الحجز</h5>
          <p><strong>رقم الحجز:</strong> {{ device.reservation_number ?? '-' }}</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5 class="mb-3">الحالة الحالية</h5>
          <p>
            <span v-if="device.status === 'suspended'" class="status-badge status-badge--suspended">🔴 معلق</span>
            <span v-else class="status-badge status-badge--active">🟢 نشط</span>
          </p>
        </div>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <div class="card p-3 h-100">
          <h5 class="mb-3">الجهاز الحالي</h5>
          <p><strong>الماركة:</strong> {{ device.brand ?? '-' }}</p>
          <p><strong>الشركة المصنعة:</strong> {{ device.manufacturer ?? '-' }}</p>
          <p><strong>الموديل:</strong> {{ device.model ?? '-' }}</p>
          <p><strong>اسم الجهاز:</strong> {{ device.device_name ?? '-' }}</p>
          <p><strong>Android ID:</strong> {{ device.android_id ?? '-' }}</p>
          <p><strong>Device UUID:</strong> {{ device.device_uuid ?? '-' }}</p>
          <p><strong>إصدار أندرويد:</strong> {{ device.android_version ?? '-' }}</p>
          <p><strong>SDK:</strong> {{ device.sdk_version ?? '-' }}</p>
          <p><strong>المنصة:</strong> {{ device.platform ?? '-' }}</p>
          <p><strong>اسم الحزمة:</strong> {{ device.package_name ?? '-' }}</p>
          <p><strong>إصدار التطبيق الحالي:</strong> {{ device.app_version ?? '-' }}</p>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card p-3 h-100">
          <h5 class="mb-3">بيانات الدخول</h5>
          <p><strong>آخر دخول:</strong> {{ formatRelativeTime(device.last_login_at) }}</p>
          <p><strong>آخر ظهور:</strong> {{ formatRelativeTime(device.last_seen_at) }}</p>
          <p><strong>آخر IP:</strong> {{ device.last_ip ?? '-' }}</p>
        </div>
      </div>
    </div>

    <div class="card p-3">
      <h5 class="mb-3">سجل الجهاز (Device Timeline)</h5>
      <ul class="device-timeline" v-if="device.history && device.history.length">
        <li v-for="h in device.history" :key="h.id" class="device-timeline__item">
          <span class="device-timeline__dot" :class="`device-timeline__dot--${actionColor(h.action)}`">
            <i class="pi" :class="actionIcon(h.action)"></i>
          </span>
          <div class="device-timeline__content">
            <div class="device-timeline__header">
              <strong>{{ actionLabel(h) }}</strong>
              <span class="device-timeline__date">{{ h.created_at }}</span>
            </div>
            <div class="device-timeline__meta">
              <span>المسؤول: {{ h.admin ?? 'النظام' }}</span>
              <span v-if="h.reason">السبب: {{ reasonLabel(h.reason) }}</span>
              <span v-if="h.custom_reason">تفصيل: {{ h.custom_reason }}</span>
              <span v-if="h.previous_status || h.new_status">
                الحالة: {{ h.previous_status ?? '-' }} ← {{ h.new_status ?? '-' }}
              </span>
              <span v-if="h.previous_app_version || h.new_app_version">
                الإصدار: {{ h.previous_app_version ?? '-' }} ← {{ h.new_app_version ?? '-' }}
              </span>
            </div>
          </div>
        </li>
      </ul>
      <div v-else class="text-center py-4">لا يوجد سجل بعد</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { formatRelativeTime } from '@/utils/relativeTime.js';

const ACTION_LABELS = {
  device_registered: 'تسجيل جهاز جديد',
  login: 'تسجيل الدخول',
  suspend: 'تعليق الجهاز',
  reactivate: 'إعادة تفعيل الجهاز',
  reset: 'إعادة ضبط الجهاز',
  change_approved: 'اعتماد طلب تغيير الجهاز',
  change_rejected: 'رفض طلب تغيير الجهاز',
  app_updated: 'تحديث التطبيق',
  force_logout: 'تسجيل خروج إجباري',
};

const ACTION_ICONS = {
  device_registered: 'pi-plus-circle',
  login: 'pi-sign-in',
  suspend: 'pi-pause-circle',
  reactivate: 'pi-play-circle',
  reset: 'pi-refresh',
  change_approved: 'pi-sync',
  change_rejected: 'pi-times-circle',
  app_updated: 'pi-arrow-up-right',
  force_logout: 'pi-sign-out',
};

const ACTION_COLORS = {
  device_registered: 'blue',
  login: 'green',
  suspend: 'orange',
  reactivate: 'green',
  reset: 'red',
  change_approved: 'blue',
  change_rejected: 'red',
  app_updated: 'blue',
  force_logout: 'orange',
};

export default {
  name: 'DeviceDetails',
  components: { Breadcrumb },
  data() {
    return {
      device: null,
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'الأجهزة المسجلة', path: '/students/devices' },
        { name: 'تفاصيل الجهاز', path: '#' },
      ],
    };
  },
  methods: {
    formatRelativeTime,
    actionLabel(h) {
      return ACTION_LABELS[h.action] ?? h.action;
    },
    actionIcon(action) {
      return ACTION_ICONS[action] ?? 'pi-circle';
    },
    actionColor(action) {
      return ACTION_COLORS[action] ?? 'gray';
    },
    reasonLabel(reason) {
      if (!reason) return '-';
      const map = {
        account_sharing: 'مشاركة الحساب',
        lost_phone: 'فقدان الهاتف',
        suspicious_activity: 'نشاط مشبوه',
        temporary_suspension: 'تعليق مؤقت',
        device_under_investigation: 'الجهاز قيد التحقيق',
        student_requested_new_phone: 'الطالب طلب هاتفاً جديداً',
        phone_replaced: 'تم استبدال الهاتف',
        phone_sold: 'تم بيع الهاتف',
        factory_reset: 'تمت إعادة ضبط المصنع',
        damaged_device: 'الجهاز تالف',
        other: 'أخرى',
      };
      return map[reason] ?? reason;
    },
    fetchDevice() {
      axios.get(`/devices/${this.$route.params.id}`).then(({ data }) => {
        this.device = data.data;
      });
    },
  },
  mounted() {
    this.fetchDevice();
  },
};
</script>

<style scoped>
.device-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
}

.device-timeline__item {
  position: relative;
  display: flex;
  gap: 0.85rem;
  padding-bottom: 1.5rem;
}

.device-timeline__item:not(:last-child)::before {
  content: '';
  position: absolute;
  top: 2rem;
  bottom: 0;
  right: 0.85rem;
  width: 2px;
  background: #e5e7eb;
}

.device-timeline__dot {
  flex: 0 0 auto;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #fff;
  z-index: 1;
}

.device-timeline__dot--blue { background: #3b82f6; }
.device-timeline__dot--green { background: #22c55e; }
.device-timeline__dot--orange { background: #f59e0b; }
.device-timeline__dot--red { background: #ef4444; }
.device-timeline__dot--gray { background: #9ca3af; }

.device-timeline__content {
  flex: 1;
  background: #f9fafb;
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
}

.device-timeline__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.device-timeline__date {
  color: #6b7280;
  font-size: 0.85rem;
}

.device-timeline__meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-top: 0.35rem;
  color: #4b5563;
  font-size: 0.85rem;
}
</style>
